import React, { useCallback, useEffect, useMemo } from "react";
import { FieldType } from "shared/types/field";
import {
  EditableInputComponent,
  SectionEditableField,
  SectionHeader,
} from "..";

type SectionProps = {
  title: string;
  icon: any;
  fieldsConfig: any[];
  editMode: boolean;
  isLoading: boolean;
  onEditClick?: () => void;
  onSaveClick?: () => void;
  editedState?: any;
  originalState: any;
  setStateFunction?: (prev: any) => void;
  showEditToggle?: boolean;
};

type FieldProps = {
  fieldConfig: any;
  editedState: any;
  originalState: any;
  editMode: boolean;
  setStateFunction: (prev: any) => void;
  isLoading: boolean;
};

const RenderEditableFieldComponentBase: React.FC<FieldProps> = ({
  fieldConfig,
  editedState,
  originalState,
  editMode,
  setStateFunction,
  isLoading,
}) => {
  const {
    label,
    key,
    type = FieldType.DEFAULT,
    Component = EditableInputComponent,
    mask = "",
    indentation = null,
    style = "regular",
    disableEditMode = false,
    link = "",
    linkTooltip = "",
  } = fieldConfig;

  const finalEditMode = disableEditMode ? false : editMode;

  // Determine the value, considering empty strings
  const value = useMemo(() => {
    let val = editedState?.[key];
    if (val === undefined) {
      val = originalState?.[key] || "";
    }
    return val;
  }, [editedState?.[key], originalState?.[key], key]);

  const handleChange = useCallback((newValue: any) => {
    setStateFunction((prev: any) => ({ ...prev, [key]: newValue }));
  }, [setStateFunction, key]);

  const componentToRender = useMemo(() => (
    <Component
      label={label}
      type={type}
      editMode={finalEditMode}
      value={value}
      mask={mask}
      indentation={indentation}
      onChange={handleChange}
      isLoading={isLoading}
      style={style}
    />
  ), [Component, label, type, finalEditMode, value, mask, indentation, handleChange, isLoading, style]);

  return (
    <SectionEditableField
      key={key}
      label={label}
      type={type}
      indentation={indentation}
      ComponentToRender={componentToRender}
      editMode={finalEditMode}
      value={value}
      mask={mask}
      onChange={handleChange}
      isLoading={isLoading}
      style={style}
      link={link}
      linkTooltip={linkTooltip}
    />
  );
};

// Memoized component that only re-renders when key or value changes
export const RenderEditableFieldComponent = React.memo(
  RenderEditableFieldComponentBase,
  (prevProps, nextProps) => {
    const { fieldConfig: prevFieldConfig, editedState: prevEditedState, originalState: prevOriginalState, editMode: prevEditMode, isLoading: prevIsLoading } = prevProps;
    const { fieldConfig: nextFieldConfig, editedState: nextEditedState, originalState: nextOriginalState, editMode: nextEditMode, isLoading: nextIsLoading } = nextProps;
    
    // Check if field key changed
    if (prevFieldConfig.key !== nextFieldConfig.key) {
      return false; // Re-render
    }
    
    // Check if the actual value for this field changed
    const prevValue = prevEditedState?.[prevFieldConfig.key] !== undefined 
      ? prevEditedState[prevFieldConfig.key] 
      : prevOriginalState?.[prevFieldConfig.key] || "";
    const nextValue = nextEditedState?.[nextFieldConfig.key] !== undefined 
      ? nextEditedState[nextFieldConfig.key] 
      : nextOriginalState?.[nextFieldConfig.key] || "";
    
    if (prevValue !== nextValue) {
      return false; // Re-render
    }
    
    // Check if editMode changed
    if (prevEditMode !== nextEditMode) {
      return false; // Re-render
    }
    
    // Check if isLoading changed
    if (prevIsLoading !== nextIsLoading) {
      return false; // Re-render
    }
    
    return true; // Don't re-render
  }
);

const RenderSectionComponentBase: React.FC<SectionProps> = ({
  title,
  icon,
  fieldsConfig,
  editMode,
  onEditClick,
  onSaveClick,
  editedState,
  originalState,
  setStateFunction = () => {},
  isLoading,
  showEditToggle = true,
}) => {
  // Memoize fieldsConfig to ensure stability and prevent mutations
  const stableFieldsConfig = useMemo(() => {
    // Deep freeze the config to prevent any mutations
    const frozenConfig = fieldsConfig.map(config => Object.freeze({ ...config }));
    return frozenConfig; // Remove Object.freeze to avoid readonly issues
  }, [fieldsConfig]);

  useEffect(() => {
    console.log("DOING NOTHING 8");
  }, [stableFieldsConfig]);
  
  return (
  <div
    key={title}
    className="divide-y divide-neutral-primary/15 rounded-md border border-neutral-primary/15 bg-white"
  >
    <SectionHeader
      title={title}
      icon={icon}
      editMode={editMode}
      onEditClick={onEditClick}
      onSaveClick={onSaveClick}
      showEditToggle={showEditToggle}
    />
          {stableFieldsConfig.map((fieldConfig: any) => (
      <RenderEditableFieldComponent
        key={fieldConfig.key}
        fieldConfig={fieldConfig}
        editMode={editMode}
        editedState={editedState}
        originalState={originalState}
        setStateFunction={setStateFunction}
        isLoading={isLoading}
      />
    ))}
  </div>
)};

// Memoized section component that only re-renders when fieldsConfig keys change
export const RenderSectionComponent = React.memo(
  RenderSectionComponentBase,
  (prevProps, nextProps) => {
    // Check if basic props changed
    if (
      prevProps.title !== nextProps.title ||
      prevProps.editMode !== nextProps.editMode ||
      prevProps.isLoading !== nextProps.isLoading ||
      prevProps.showEditToggle !== nextProps.showEditToggle
    ) {
      return false; // Re-render
    }

    // Check if fieldsConfig array length changed
    if (prevProps.fieldsConfig.length !== nextProps.fieldsConfig.length) {
      return false; // Re-render
    }

    // Check if fieldsConfig keys changed (order or content)
    const prevKeys = prevProps.fieldsConfig.map((config: any) => config.key);
    const nextKeys = nextProps.fieldsConfig.map((config: any) => config.key);
    
    for (let i = 0; i < prevKeys.length; i++) {
      if (prevKeys[i] !== nextKeys[i]) {
        console.log("DOING NOTHING 3");
        return false; // Re-render
      }
    }

    // Check if any field values changed for the keys in this section
    for (const fieldConfig of prevProps.fieldsConfig) {
      const key = fieldConfig.key;
      const prevValue = prevProps.editedState?.[key] !== undefined 
        ? prevProps.editedState[key] 
        : prevProps.originalState?.[key] || "";
      const nextValue = nextProps.editedState?.[key] !== undefined 
        ? nextProps.editedState[key] 
        : nextProps.originalState?.[key] || "";
      
      if (prevValue !== nextValue) {
        return false; // Re-render
      }
    }

    return true; // Don't re-render
  }
);
